import { MAX_COL_SIZE, MIN_COL_SIZE } from '../constants';
import {
  CustomColumnDef,
  ParsedWbColumnWidth,
  WbColumnWidthType,
} from '../types';

export function clampColumnWidth(
  width: number,
  maxWidth = MAX_COL_SIZE,
  minWidth = MIN_COL_SIZE,
) {
  let w = width;
  if (maxWidth) w = Math.max(w, minWidth);
  if (minWidth) w = Math.min(w, maxWidth);
  return w;
}

// the minimal information making up a column sizing config
export type CompactColumnSizingConfig = {
  id: string;
  width: ParsedWbColumnWidth;
  minWidth?: number;
  maxWidth?: number;
  autoMinWidth?: number;
  autoMaxWidth?: number;
};

function extractColumnCompactConfig<T>(
  column: CustomColumnDef<T>,
): CompactColumnSizingConfig {
  return {
    id: column.id,
    width: column.parsedWidth,
    minWidth: column.parsedMinWidth,
    maxWidth: column.parsedMaxWidth,
    autoMinWidth: column.parsedAutoMinWidth,
    autoMaxWidth: column.parsedAutoMaxWidth,
  };
}

export function getCompactSizingConfigId<T>(columns: CustomColumnDef<T>[]) {
  return JSON.stringify(columns?.map(c => extractColumnCompactConfig(c)) ?? []);
}

export function expandCompactSizingConfigId(compactConfig: string) {
  return JSON.parse(compactConfig) as CompactColumnSizingConfig[];
}

/**
 * Normalizes a string to be safely used as a CSS custom property identifier.
 * - Replaces any invalid char with '-' (only [A-Za-z0-9_-] are kept)
 */
const normalizeForCSSVariable = (variable: string): string => {
  const v = variable.replace(/[^A-Za-z0-9_-]+/g, '_'); // hyphen at end of class to avoid forming a range
  return v;
};

export function getColWidthVarNameFromId(colId: string) {
  return `--col-${normalizeForCSSVariable(colId)}-width`;
}

export function getColMinWidthVarNameFromId(colId: string) {
  return `--col-${normalizeForCSSVariable(colId)}-min-width`;
}

export function getColMaxWidthVarNameFromId(colId: string) {
  return `--col-${normalizeForCSSVariable(colId)}-max-width`;
}

export function getCellWidthStyle(colId: string): React.CSSProperties {
  return {
    width: `var(${getColWidthVarNameFromId(colId)})`,
    minWidth: `var(${getColMinWidthVarNameFromId(colId)})`,
    maxWidth: `var(${getColMaxWidthVarNameFromId(colId)})`,
  };
}

export type ColumnMeasures = Record<string, number>;

/**
 * Compute a fixed (numeric) width for every column in the table, distributing equally the free space between the columns with no defined width
 * @param columns the table column configs
 * @param context the metadata needed to compute the layout, containing current container width, current resized state
 * @returns
 */
export function computeFixedColumnWidths(
  columns: CompactColumnSizingConfig[],
  context: {
    tableWidth: number;
    resizingState: Record<string, number>;
    resizedColumns: Set<string>;
  },
): Record<string, number> {
  const { tableWidth, resizingState, resizedColumns } = context;
  let totalFixedWidth = 0;
  let totalColumnsWithFixedWidth = 0;

  const finalWidthsById: Record<string, number> = {};

  const fillingSpaceCols: string[] = [];

  columns.forEach(c => {
    const width = c.width;

    let computedWidth: number | undefined = undefined;

    const minWidth = c.minWidth;
    const maxWidth = c.maxWidth;

    switch (width.type) {
      case WbColumnWidthType.FIXED:
        computedWidth = width.value;
        break;
      case WbColumnWidthType.PERCENT:
        computedWidth = Math.floor((tableWidth / 100) * width.value);
        break;
      // auto is not supported in fixed layout and will be treated the same as not defined,
      // every column must be assigned a fixed numeric px value in fixed layout
      case WbColumnWidthType.AUTO:
      case WbColumnWidthType.NOT_DEFINED:
      default:
        computedWidth = undefined;
    }
    const isResized = resizedColumns.has(c.id) && c.id in resizingState;

    if (isResized) {
      // tanstack stores column size non clamped in resize state
      computedWidth = clampColumnWidth(resizingState[c.id], minWidth, maxWidth);
    }

    if (computedWidth !== undefined) {
      const clampedWidth = clampColumnWidth(computedWidth, maxWidth, minWidth);
      finalWidthsById[c.id] = clampedWidth;
      totalFixedWidth += clampedWidth;
      totalColumnsWithFixedWidth++;
    } else fillingSpaceCols.push(c.id);
  });

  // distribute remaining space between columns with no assigned size
  const splitColWidth = Math.floor(
    Math.max(
      (tableWidth - totalFixedWidth) /
        Math.max(columns.length - totalColumnsWithFixedWidth, 1),
      0,
    ),
  );

  fillingSpaceCols.forEach(cId => (finalWidthsById[cId] = splitColWidth));

  return finalWidthsById;
}

class ColumnMeasureError extends Error {
  constructor() {
    super('Could not measure table columns');
  }
}

export function measureHeadColumns(
  headEl: HTMLTableSectionElement,
): ColumnMeasures {
  const columnMeasures: ColumnMeasures = {};

  const ths = headEl.querySelectorAll<HTMLTableCellElement>('th');
  ths.forEach(th => {
    const width = th.getBoundingClientRect().width;
    const id = th.getAttribute('data-id');
    if (!id) return;
    columnMeasures[id] = width;
  });

  return columnMeasures;
}

export function measureTableColumns(tableEl: HTMLTableElement): ColumnMeasures {
  if (!tableEl) throw new ColumnMeasureError();

  const thead = tableEl.querySelector<HTMLTableSectionElement>('thead');

  if (!thead) throw new ColumnMeasureError();

  return measureHeadColumns(thead);
}
